(() => {
  if (window.BBQuickAccessReady) return;
  window.BBQuickAccessReady = true;
  let activeDrawer = null;
  let generation = 0;
  let stopProximity = null;
  const animations = new Set();
  const cancelAnimations = () => { animations.forEach(animation => animation.cancel()); animations.clear(); };
  const animate = (element, frames, kind, delay = 0, duration) => {
    const motion = window.BBMotion?.play(element, frames, kind, delay);
    if (motion) {
      if (duration !== undefined) motion.effect.updateTiming({ duration });
      animations.add(motion);
      motion.finished.then(() => animations.delete(motion)).catch(() => animations.delete(motion));
    }
    return motion;
  };
  const mobile = drawer => drawer.classList.contains('bb-quick-access--mobile');
  const exitFrame = () => ({ opacity: 1, translate: '-100% 0' });
  const settled = { opacity: 1, translate: '0 0' };
  // React Bits LineSidebar's smooth vertical proximity response, scoped to the
  // existing desktop links. The menu markup and navigation remain untouched.
  const startProximity = drawer => {
    if (mobile(drawer) || !matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)').matches) return null;
    const panel = drawer.querySelector('.bb-quick-access__drawer');
    const rows = [...panel.querySelectorAll('.bb-quick-access__row, .bb-quick-access__subrow, .bb-quick-access__secondary-row')];
    const targets = rows.map(() => 0);
    const current = rows.map(() => 0);
    let frame = 0;
    let last = 0;
    let pointerY = null, pointerDirty = false;
    const written = rows.map(() => '');
    const tick = now => {
      if (pointerDirty) {
        pointerDirty = false;
        // Read geometry once per frame, before writing any proximity styles.
        rows.forEach((row, index) => {
          const rect = row.getBoundingClientRect();
          const p = pointerY === null || !rect.width || !rect.height ? 0 : Math.max(0, 1 - Math.abs(pointerY - (rect.top + rect.height / 2)) / 100);
          targets[index] = p * p * (3 - 2 * p);
        });
      }
      const dt = Math.min((now - last) / 1000, .05);
      last = now;
      const ease = 1 - Math.exp(-dt / .1);
      let moving = false;
      rows.forEach((row, index) => {
        const next = current[index] + (targets[index] - current[index]) * ease;
        const settled = Math.abs(targets[index] - next) < .0015;
        current[index] = settled ? targets[index] : next;
        const value = current[index].toFixed(4);
        if (written[index] !== value) { row.style.setProperty('--bb-line-effect', value); written[index] = value; }
        if (!settled) moving = true;
      });
      frame = moving ? requestAnimationFrame(tick) : 0;
    };
    const queue = () => { if (!frame) { last = performance.now(); frame = requestAnimationFrame(tick); } };
    const move = event => {
      if (event.pointerType === 'touch') return;
      pointerY = event.clientY; pointerDirty = true;
      queue();
    };
    const leave = () => { pointerY = null; pointerDirty = true; targets.fill(0); queue(); };
    panel.addEventListener('pointermove', move, { passive: true });
    panel.addEventListener('pointerleave', leave, { passive: true });
    return () => {
      panel.removeEventListener('pointermove', move);
      panel.removeEventListener('pointerleave', leave);
      if (frame) cancelAnimationFrame(frame);
      rows.forEach(row => row.style.removeProperty('--bb-line-effect'));
    };
  };
  const close = (restoreFocus = true, withMotion = true) => {
    const drawer = activeDrawer;
    if (!drawer) return;
    const revision = ++generation;
    const panel = drawer.querySelector('.bb-quick-access__drawer');
    const style = getComputedStyle(panel);
    const current = { opacity: style.opacity, translate: style.translate };
    stopProximity?.();
    stopProximity = null;
    cancelAnimations();
    const finish = () => {
      if (revision !== generation) return;
      drawer.open = false;
      drawer.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('bb-quick-access-open');
      activeDrawer = null;
      if (restoreFocus) drawer.querySelector(':scope > summary')?.focus({ preventScroll: true });
    };
    const motion = withMotion ? animate(panel, [current, exitFrame(drawer)], 'menu-close') : null;
    if (motion) motion.finished.then(finish).catch(() => {});
    else finish();
  };
  const open = drawer => {
    close(false, false);
    ++generation;
    activeDrawer = drawer;
    drawer.open = true;
    drawer.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('bb-quick-access-open');
    const panel = drawer.querySelector('.bb-quick-access__drawer');
    panel.scrollTop = 0;
    stopProximity = startProximity(drawer);
    animate(panel, [exitFrame(drawer), settled], 'menu-open');
    const tokens = window.BBMotion?.tokens;
    if (tokens) {
      // Reference motion only: a quick panel slide, then a tight left-to-right
      // fade on each primary link in DOM order. Header/lower links ride the panel.
      const rows = [...panel.querySelectorAll('.bb-quick-access__primary > :is(a, .bb-quick-access__collection-heading)')];
      const step = Math.min(100, 1500 / Math.max(rows.length, 1));
      rows.forEach((row, index) => {
        animate(row, [{ opacity: 0, translate: '-10px 0' }, settled], 'state', 200 + index * step, 200);
      });
    }
    drawer.querySelector('.bb-quick-access__close')?.focus({ preventScroll: true });
  };
  // Delegated once: Shopify section replacement cannot accumulate listeners.
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const submenuToggle = event.target.closest('[data-bb-collections-toggle]');
    if (submenuToggle) {
      const submenu = document.getElementById(submenuToggle.getAttribute('aria-controls'));
      if (submenu) {
        const expanded = submenuToggle.getAttribute('aria-expanded') !== 'true';
        submenuToggle.setAttribute('aria-expanded', String(expanded));
        submenuToggle.setAttribute('aria-label', (expanded ? 'Collapse' : 'Expand') + ' collections');
        submenu.querySelectorAll('a').forEach(row => row.getAnimations().forEach(animation => animation.cancel()));
        submenu.hidden = !expanded;
        if (expanded && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          submenu.querySelectorAll('a').forEach((row, index) => {
            animate(row, [{ opacity: 0, translate: '-10px 0' }, settled], 'state', index * 100, 200);
          });
        }
      }
      return;
    }
    const trigger = event.target.closest('[data-bb-quick-access] > summary');
    if (trigger) {
      event.preventDefault();
      const drawer = trigger.parentElement;
      if (drawer.open) close(); else open(drawer);
      return;
    }
    if (event.target.closest('[data-bb-quick-access-close]')) close();
    else if (activeDrawer?.contains(event.target) && event.target.closest('a[href]')) close(false, false);
  });
  document.addEventListener('keydown', event => {
    if (!activeDrawer) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (event.key === 'Tab') {
      const controls = [...activeDrawer.querySelector('.bb-quick-access__drawer').querySelectorAll('a[href],button')].filter(control => control.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });
  matchMedia('(max-width: 989px)').addEventListener('change', () => close(false, false));
  matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => close(false, false));
  window.addEventListener('pageshow', () => close(false, false));
  document.addEventListener('shopify:section:unload', event => { if (event.target.contains(activeDrawer)) close(false, false); });
  document.addEventListener('bb:close-menu', () => close(false, false));
})();
